import { Controller } from "stimulus";
import { formatMoney, toMinorUnits } from "utils/money";
import { getJson, requestJson } from "utils/json_request";

// The tip composer. Its element wraps the tip panel + the input form, so it owns the heart toggle,
// relabels the shared send button, and routes a submit to a tip (here) or a plain chat message
// (delegated to the sibling `chat-panel` controller). The Stripe.js flow runs in place; the pin
// arrives as a tip room event.
export default class ChatTip extends Controller {
  static targets = ["panel", "hint", "preset", "custom", "cardSection", "cardElement", "error", "sendButton", "sendIcon", "sendLabel", "heartButton", "walletSection", "walletElement", "disclosure"];

  static values = {
    contextType: String,
    contextId: Number,
    account: String,
    publicKey: String,
    currency: String,
    createUrl: String,
    minCents: Number,
    maxCents: Number,
    currentUserId: Number,
    country: {type: String, default: "US"},
    hasCardOnFile: Boolean,
    // The native apps, where the panel is an explanation and nothing else: no amount targets, no
    // Stripe values, and the form's submit goes straight to chat-panel. Only toggle/close are wired.
    noticeOnly: Boolean,
  };

  connect() {
    this.open = false;
    this.presetCents = null; // amount from a preset chip (custom takes precedence when typed)
    this.selectedCents = null; // effective amount, or null for a normal (non-tip) message
    this.clientTipId = null; // stable per charge attempt; rotated only on a definitive succeed/decline
    this.chargedCents = null; // amount last submitted under clientTipId; changing it rotates the key
    this.chargedPaymentMethodId = null; // the paymentMethodId (if any) submitted under clientTipId
    this.amountLocked = false; // set while an ambiguous/settling charge may have succeeded (see lockAmount)
    this.cardMounted = false;
    this.cardRevealed = false; // once true, submits collect a card via Elements instead of card-on-file
    this.inFlight = false;
    this.paymentMethodLoad = null; // the once-per-connection card-on-file fetch, see setPanelOpen
    this.stripeWarmUp = null;
    this.walletProbeDone = false; // true once canMakePayment() has resolved once (see ensureWalletMounted)
    this.walletAvailable = false; // true once canMakePayment() resolves truthy
    this.walletLabel = null; // "Apple Pay" / "Google Pay", set once wallet availability is known
    this.paymentRequest = null;
    // The server-rendered copy, cached so switching away from the wallet button can restore it verbatim.
    if (this.hasDisclosureTarget) this.defaultDisclosureText = this.disclosureCopyText();
    // Notice-only stops here: refresh(), and every method it leads to, reads composer targets that
    // this mode never renders. The state above is still set so nothing downstream sees undefined.
    if (this.noticeOnlyValue) return;
    this.refresh();
  }

  disconnect() {
    if (this.cardEl) {
      this.cardEl.destroy();
      this.cardEl = null;
    }
    if (this.walletButtonEl) {
      this.walletButtonEl.destroy();
      this.walletButtonEl = null;
    }
    this.clearStripeWait();
  }

  // --- panel + amount selection ---------------------------------------------

  toggle() {
    if (this.open) this.close();
    else this.setPanelOpen(true);
  }

  // Collapsing is always allowed - it's the only way out of a lock that no pin ever clears. Dropping
  // the amount is not: an in-flight or settling attempt still needs it, because submit() reads
  // selectedCents to route a retry to the tip path and lockAmount freezes whatever is selected.
  close() {
    this.setPanelOpen(false);
    if (!this.noticeOnlyValue && !this.inFlight && !this.amountLocked) this.resetComposer();
    if (this.hasHeartButtonTarget) this.heartButtonTarget.focus(); // the panel we sat in is now hidden
  }

  setPanelOpen(value) {
    this.open = value;
    this.panelTarget.classList.toggle("hidden", !value);
    if (this.hasHeartButtonTarget) {
      this.heartButtonTarget.classList.toggle("bg-red-400/20", value);
      this.heartButtonTarget.setAttribute("aria-expanded", String(value));
    }
    // Deferred to the first open (not connect()) so a viewer who never taps the heart never pays
    // for the card lookup or the wallet probe. The card lookup runs once; Stripe.js warms up beside
    // it, and every open then waits only on the lookup before the wallet probe (skipped once a card
    // is on file, since the wallet button would never show anyway - see updateWalletVisibility).
    if (value && !this.noticeOnlyValue) {
      this.paymentMethodLoad ||= this.loadPaymentMethod();
      this.stripeWarmUp ||= this.ensureStripe().catch(() => {});
      this.paymentMethodLoad.then(() => this.stripeWarmUp).then(() => {
        if (this.open && !this.hasCardOnFileValue) this.ensureWalletMounted().catch(() => {});
      });
    }
  }

  // The card on file is read when the sheet first opens, not during the page render (a Stripe read
  // per viewer). A failed fetch keeps the no-card behaviour: generic sentence, wallet probe runs.
  async loadPaymentMethod() {
    if (!this.hasPaymentMethodUrlValue) return;

    try {
      const data = await getJson(this.paymentMethodUrlValue + this.contextQuery());
      // Never flips an already-true flag back: a tip sent while this was in flight may have set it.
      if (data.has_card_on_file !== true) return;

      this.hasCardOnFileValue = true;
      if (data.disclosure) {
        this.defaultDisclosureText = data.disclosure;
        const el = this.disclosureCopyElement();
        if (el) el.textContent = data.disclosure;
      }
    } catch {
      // keep today's no-card behaviour
    }
  }

  contextQuery() {
    return `?${new URLSearchParams({context_type: this.contextTypeValue, context_id: this.contextIdValue})}`;
  }

  // Guard-free, so onSucceeded can share it while its charge is still marked in flight. clientTipId +
  // chargedCents deliberately survive: a thrown submit never locks, so an earlier attempt may have
  // charged and refresh()'s rotate-on-change guard must stay the only key burner.
  resetComposer() {
    this.presetCents = null;
    this.customTarget.value = "";
    this.clearError();
    this.hideCard();
    this.refresh();
  }

  selectPreset(event) {
    if (this.amountLocked) return; // an ambiguous/settling charge is pending; amount is frozen
    const cents = parseInt(event.currentTarget.dataset.tipCents, 10);
    // Tapping the selected chip clears it (back to a normal message); typing custom wins over a chip.
    this.presetCents = this.presetCents === cents ? null : cents;
    this.customTarget.value = "";
    this.refresh();
  }

  customInput() {
    if (this.amountLocked) return;
    this.presetCents = null; // a typed custom amount clears any chip
    this.refresh();
  }

  effectiveCents() {
    const custom = this.parseCustomCents();
    if (custom != null) return custom;
    return this.presetCents;
  }

  parseCustomCents() {
    const raw = (this.customTarget.value || "").trim();
    if (raw === "") return null;
    const num = parseFloat(raw);
    if (isNaN(num) || num <= 0) return null;
    return toMinorUnits(num, this.currencyValue);
  }

  refresh() {
    const custom = this.parseCustomCents();
    const customActive = custom != null;
    this.selectedCents = this.effectiveCents();

    // Switching to a different amount rotates the key: replaying a burned idempotency key with a
    // new amount trips Stripe's idempotency_error. A same-amount retry keeps the id so it dedupes
    // against the pending charge. NEVER rotate while locked - that charge may have succeeded, so a
    // retry must reuse the key to dedupe rather than mint a second charge.
    if (!this.amountLocked && this.clientTipId != null && this.chargedCents != null &&
        this.selectedCents != null && this.selectedCents !== this.chargedCents) {
      this.rotateClientTipId();
    }

    this.presetTargets.forEach((chip) => {
      const cents = parseInt(chip.dataset.tipCents, 10);
      const active = !customActive && this.presetCents === cents;
      chip.classList.toggle("border-accent", active);
      chip.classList.toggle("bg-accent", active);
      chip.classList.toggle("text-white", active);
      chip.classList.toggle("border-gray-300", !active);
      chip.classList.toggle("bg-white", !active);
      // The themed: resting classes outrank the plain active ones, so they have to go too.
      chip.classList.toggle("themed:border-light-text/30", !active);
      chip.classList.toggle("themed:bg-surface", !active);
    });

    this.customTarget.classList.toggle("border-accent", customActive);
    this.customTarget.classList.toggle("bg-accent/10", customActive);
    this.customTarget.classList.toggle("border-gray-300", !customActive);
    this.customTarget.classList.toggle("bg-white", !customActive);
    this.customTarget.classList.toggle("themed:border-light-text/30", !customActive);
    this.customTarget.classList.toggle("themed:bg-surface", !customActive);

    // Keep the in-flight paymentRequest's total in sync with every amount change; it starts life
    // with a placeholder total (see ensureWalletMounted) since Stripe needs one before any amount
    // is ever picked.
    if (this.paymentRequest && this.selectedCents != null) {
      this.paymentRequest.update({total: {label: "Tip", amount: this.selectedCents}});
    }

    this.updateSendButton();
    this.updateHint();
    this.updateWalletVisibility();
  }

  // Shown only once an amount is picked, wallet capability is confirmed, no card is already being
  // collected, and no card is on file (a card-on-file tip is already a plain one-tap send).
  updateWalletVisibility() {
    if (!this.hasWalletSectionTarget) return;

    const show = this.selectedCents != null && this.walletAvailable && !this.cardRevealed && !this.hasCardOnFileValue;
    this.walletSectionTarget.classList.toggle("hidden", !show);
    if (show) this.showWalletDisclosure();
    else this.restoreDisclosure();
  }

  updateSendButton() {
    if (!this.hasSendButtonTarget) return;
    const tipping = this.selectedCents != null;

    if (this.hasSendIconTarget) this.sendIconTarget.classList.toggle("hidden", tipping);
    if (this.hasSendLabelTarget) {
      this.sendLabelTarget.classList.toggle("hidden", !tipping);
      if (tipping) this.sendLabelTarget.textContent = `Tip ${formatMoney(this.selectedCents, this.currencyValue)}`;
    }
    // Grow from the circular icon button into a labelled pill when a tip amount is active.
    this.sendButtonTarget.classList.toggle("w-10", !tipping);
    this.sendButtonTarget.classList.toggle("w-auto", tipping);
    this.sendButtonTarget.classList.toggle("px-4", tipping);
  }

  updateHint() {
    if (!this.hasHintTarget) return;
    this.hintTarget.textContent = this.selectedCents != null
      ? `${formatMoney(this.selectedCents, this.currencyValue)} · pins your message`
      : "pick any amount";
  }

  // --- submit routing -------------------------------------------------------

  submit(event) {
    event.preventDefault();
    if (this.inFlight) return;

    if (this.selectedCents == null) {
      // No amount selected: this is a normal chat message. Hand off to the chat-panel controller
      // (it owns the room client + input clearing) so nothing about plain chat changes.
      const chatPanel = this.chatPanelController();
      if (chatPanel) {
        chatPanel.send(event);
      } else {
        // The lookup failed - surface it here rather than swallow the tap. showError() is the
        // right channel: it reveals the tip panel itself, so it doesn't depend on the controller
        // that couldn't be reached.
        this.showError("We couldn't send that message. Please reload the page and try again.");
      }
      return;
    }

    this.sendTip();
  }

  async sendTip() {
    const cents = this.selectedCents;
    if (cents < this.minCentsValue || cents > this.maxCentsValue) {
      this.showError(`Enter a tip between ${formatMoney(this.minCentsValue, this.currencyValue)} and ${formatMoney(this.maxCentsValue, this.currencyValue)}.`);
      return;
    }

    this.clearError();
    this.setInFlight(true);

    try {
      let paymentMethodId = null;
      if (this.cardRevealed) {
        paymentMethodId = await this.createPaymentMethod();
        if (!paymentMethodId) {
          this.setInFlight(false);
          return; // createPaymentMethod already surfaced the inline error
        }
      }

      const result = await this.postCreate(cents, paymentMethodId);
      await this.handleResult(result);
    } catch (error) {
      this.showError(this.stripeUnavailable(error)
        ? "Payments couldn't load. Disable any ad/script blocker and try again."
        : "Something went wrong sending your tip. Please try again.");
      this.setInFlight(false);
    }
  }

  async handleResult(result) {
    switch (result.status) {
      case "succeeded":
        this.onSucceeded();
        this.setInFlight(false);
        break;
      case "requires_action":
        await this.handle3DS(result);
        break;
      case "card_required":
        this.unlockAmount();
        await this.revealCard();
        this.showError(result.error || "Add a card to send a tip.");
        this.setInFlight(false);
        break;
      case "failed":
        // Definitive no-charge: a truly-succeeded original returns succeeded (Stripe idempotency),
        // never failed, so release any lock and rotate the burned key. Keep the message + amount.
        this.unlockAmount();
        this.rotateClientTipId();
        await this.revealCard();
        this.showError(result.error || "Your card was declined. Try a different card.");
        this.setInFlight(false);
        break;
      case "processing":
        // The charge was accepted and is settling; the webhook pins it once it clears. Lock the
        // amount (a new amount would mint a second charge) and don't rotate the key.
        this.lockAmount("Your tip is processing and will appear once it clears.");
        break;
      case "payment_error":
        // Ambiguous: the processor was unreachable, so the charge may have succeeded. Lock and keep
        // the key so a same-amount retry dedupes against the original instead of double-charging.
        this.lockAmount(result.error || "We couldn't reach the payment processor. Please try again.");
        break;
      default:
        // Other no-charge outcomes (invalid_amount / not_live / unavailable / forbidden): release
        // any lock, keep the message + amount, and surface a concise inline error. Never navigate.
        this.unlockAmount();
        this.showError(result.error || "We couldn't send that tip.");
        this.setInFlight(false);
    }
  }

  async handle3DS(result) {
    try {
      await this.ensureStripe();
      const { error, paymentIntent } = await this.stripe.confirmCardPayment(result.client_secret);

      if (error) {
        // Auth failed - a definitive no-charge outcome, so release any ambiguous lock. The pending
        // intent needs a fresh attempt (new key), and a new card often helps.
        this.unlockAmount();
        this.rotateClientTipId();
        await this.revealCard();
        this.showError(error.message || "We couldn't verify your card. Try a different card.");
        this.setInFlight(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        // The card cleared 3DS and was charged. From here the money is taken, so a confirm we
        // can't read must NOT surface as a hard failure - the webhook self-heals the pin.
        let confirmed;
        try {
          confirmed = await this.postConfirm(result.tip.id);
        } catch (error) {
          this.lockAmount("Your tip is processing and will appear shortly.");
          return;
        }

        if (confirmed.status === "succeeded") {
          this.onSucceeded();
          this.setInFlight(false);
        } else {
          // Charge already succeeded but confirm didn't return succeeded (processing / a transient
          // payment_error on retrieve): treat it as settling, not a failure. The webhook pins it.
          this.lockAmount("Your tip is processing and will appear shortly.");
        }
      } else if (paymentIntent && paymentIntent.status === "processing") {
        // Still settling: the payment_intent.succeeded webhook pins it once it clears. Lock the
        // amount (the same PI must settle, not be re-charged) rather than reveal the card.
        this.lockAmount("Your tip is processing and will appear once it clears.");
      } else {
        // Any other non-success (requires_payment_method / canceled) is a definitive no-charge:
        // release the lock, rotate, and let the tipper retry with a fresh card, like a decline.
        this.unlockAmount();
        this.rotateClientTipId();
        await this.revealCard();
        this.showError("Your card couldn't be charged. Try a different card.");
        this.setInFlight(false);
      }
    } catch (error) {
      this.showError(this.stripeUnavailable(error)
        ? "Payments couldn't load. Disable any ad/script blocker and try again."
        : "We couldn't complete the card verification. Please try again.");
      this.setInFlight(false);
    }
  }

  onSucceeded() {
    // A payment_method_id on this charge means Stripe just promoted it to the customer's default
    // (LiveTips::Create/Confirm's post-success promotion), so any next tip this session - wallet or
    // freshly-typed card alike - can skip straight to a plain one-tap send. Read before
    // rotateClientTipId() below clears it.
    if (this.chargedPaymentMethodId != null) this.hasCardOnFileValue = true;

    // Success is silent here: the pin + highlighted inline message arrive as a tip room event.
    const chatPanel = this.chatPanelController();
    if (chatPanel && chatPanel.hasInputTarget) chatPanel.clearInput();

    this.unlockAmount(); // a retry cleared the ambiguous/settling charge
    this.rotateClientTipId(); // the charge landed, so the key is spent
    this.setPanelOpen(false);
    this.resetComposer();
    // Best-effort: after an async round trip, iOS may not reopen the keyboard.
    if (chatPanel) chatPanel.focusInput();
  }

  // The chat-panel controller forwards every tip pin as a `chat-panel:tip` window event. While we
  // hold an ambiguous/settling lock, our OWN pin arriving means the charge resolved, so unlock.
  // Filtered to the current user so another viewer's tip can't clear our lock prematurely.
  onTipBroadcast(event) {
    if (!this.amountLocked) return;

    const tip = event.detail;
    if (!tip || !tip.user) return;
    if (this.hasCurrentUserIdValue && String(tip.user.id) !== String(this.currentUserIdValue)) return;

    this.unlockAmount();
    this.clearError();
    this.refresh();
  }

  // --- network --------------------------------------------------------------

  postCreate(cents, paymentMethodId) {
    const chatPanel = this.chatPanelController();
    const body = chatPanel && chatPanel.hasInputTarget ? chatPanel.inputTarget.value.trim() : "";

    this.chargedCents = cents;
    this.chargedPaymentMethodId = paymentMethodId;
    return this.postJson(this.createUrlValue, {
      context_type: this.contextTypeValue,
      context_id: this.contextIdValue,
      amount_cents: cents,
      body: body,
      client_tip_id: this.ensureClientTipId(),
      payment_method_id: paymentMethodId,
    });
  }

  postConfirm(tipId) {
    return this.postJson(`${this.createUrlValue}/${tipId}/confirm`, {
      context_type: this.contextTypeValue,
      context_id: this.contextIdValue,
    });
  }

  // The endpoint returns structured JSON for every expected outcome, so the body is returned
  // regardless of HTTP status; requestJson throws for a non-JSON body or a network failure.
  async postJson(url, payload) {
    const { payload: json } = await requestJson(url, { method: "POST", body: payload });
    return json;
  }

  // --- Stripe.js ------------------------------------------------------------

  async ensureStripe() {
    if (this.stripe) return;
    // Memoized so concurrent callers (e.g. two rapid panel opens before Stripe.js has loaded)
    // all await the same poll instead of each starting its own setInterval - only one of which
    // would ever get cleared (see clearStripeWait). Cleared on a rejection too, so a later retry
    // (e.g. a fresh panel open after the ~10s timeout) starts a new poll rather than replaying a
    // dead promise.
    if (!this.stripeReadyPromise) {
      this.stripeReadyPromise = this.waitForStripe().catch((error) => {
        this.stripeReadyPromise = null;
        throw error;
      });
    }
    await this.stripeReadyPromise;
    // Scope Stripe to the creator's CONNECTED account so the card + PaymentMethod belong to it.
    this.stripe = window.Stripe(this.publicKeyValue, { stripeAccount: this.accountValue });
    this.elements = this.stripe.elements();
  }

  // Poll for Stripe.js, giving up after ~10s so a blocked script never leaves the shared send
  // button disabled forever. The interval id is held so disconnect() can clear a poll in flight.
  waitForStripe() {
    return new Promise((resolve, reject) => {
      if (window.Stripe) return resolve();
      const startedAt = Date.now();
      this.stripeWaitInterval = setInterval(() => {
        if (window.Stripe) {
          this.clearStripeWait();
          resolve();
        } else if (Date.now() - startedAt > 10000) {
          this.clearStripeWait();
          reject(new Error("stripe_unavailable"));
        }
      }, 100);
    });
  }

  clearStripeWait() {
    if (this.stripeWaitInterval) {
      clearInterval(this.stripeWaitInterval);
      this.stripeWaitInterval = null;
    }
  }

  async ensureCardMounted() {
    await this.ensureStripe();
    if (this.cardMounted) return;

    this.cardEl = this.elements.create("card");
    this.cardEl.mount(this.cardElementTarget);
    this.cardEl.addEventListener("change", (e) => {
      if (e.error) this.showError(e.error.message);
      else if (e.complete) this.clearError();
    });
    this.cardMounted = true;
  }

  async revealCard() {
    await this.ensureCardMounted();
    this.cardSectionTarget.classList.remove("hidden");
    this.cardRevealed = true;
    this.updateWalletVisibility(); // the wallet button and an open card field never show at once
  }

  // Clear the Elements field too - it stays mounted while hidden, so entered digits would otherwise
  // sit in a display:none iframe and repopulate the next time a card is asked for.
  hideCard() {
    this.cardSectionTarget.classList.add("hidden");
    this.cardRevealed = false;
    if (this.cardEl) this.cardEl.clear();
  }

  async createPaymentMethod() {
    await this.ensureCardMounted();
    const { error, paymentMethod } = await this.stripe.createPaymentMethod({ type: "card", card: this.cardEl });
    if (error) {
      this.showError(error.message || "Please check your card details.");
      return null;
    }
    return paymentMethod.id;
  }

  // --- Apple Pay / Google Pay ------------------------------------------------

  // Built off the shared Stripe instance from ensureStripe() - never a second stripe.elements().
  async ensureWalletMounted() {
    await this.ensureStripe();

    if (this.walletProbeDone) {
      // The one-time canMakePayment() probe below already ran. If it found no wallet, the card
      // field was revealed proactively - but closing the panel without tipping hides it again
      // (resetComposer -> hideCard), so a reopen must re-reveal it rather than leave the panel
      // with no payment field at all. Never re-touch this.stripe.paymentRequest(...) here.
      if (!this.walletAvailable && !this.cardRevealed) await this.revealCard();
      return;
    }
    this.walletProbeDone = true; // guards only the one-time construction below, never re-run

    // Stripe requires a concrete total at construction time even though no amount may be picked
    // yet; 500 (the middle $2/$5/$10 preset) is a placeholder only - refresh() overwrites it with
    // the real amount before the button can ever be shown.
    this.paymentRequest = this.stripe.paymentRequest({
      total: {label: "Tip", amount: 500},
      country: this.countryValue,
      currency: this.currencyValue,
      disableWallets: ["link"],
    });
    this.paymentRequest.on("paymentmethod", (ev) => this.handleWalletPaymentMethod(ev));

    const canPay = await this.paymentRequest.canMakePayment();
    if (canPay && (canPay.applePay || canPay.googlePay)) {
      this.walletLabel = canPay.applePay ? "Apple Pay" : "Google Pay";
      this.walletButtonEl = this.elements.create("paymentRequestButton", {paymentRequest: this.paymentRequest});
      this.walletButtonEl.mount(this.walletElementTarget);
      this.walletAvailable = true;
      this.updateWalletVisibility();
    } else {
      // No wallet on this browser/device - reveal the card field proactively instead of waiting
      // for a failed charge attempt to discover the same thing.
      await this.revealCard();
    }
  }

  async handleWalletPaymentMethod(ev) {
    if (this.amountLocked || this.inFlight) {
      ev.complete("fail");
      return;
    }

    const cents = this.selectedCents;
    if (cents == null || cents < this.minCentsValue || cents > this.maxCentsValue) {
      ev.complete("fail");
      this.showError(`Enter a tip between ${formatMoney(this.minCentsValue, this.currencyValue)} and ${formatMoney(this.maxCentsValue, this.currencyValue)}.`);
      return;
    }

    this.clearError();
    this.setInFlight(true);

    let result;
    try {
      result = await this.postCreate(cents, ev.paymentMethod.id);
    } catch (error) {
      ev.complete("fail");
      this.showError(this.stripeUnavailable(error)
        ? "Payments couldn't load. Disable any ad/script blocker and try again."
        : "Something went wrong sending your tip. Please try again.");
      this.setInFlight(false);
      return;
    }

    // Dismiss the sheet before any further work: requires_action must run its 3DS challenge
    // in-page, not inside the (already-closed) wallet sheet, which errors out after ~30s if left
    // hanging.
    const dismissAsSuccess = ["succeeded", "processing", "requires_action"].includes(result.status);
    ev.complete(dismissAsSuccess ? "success" : "fail");

    await this.handleResult(result);
  }

  async switchToCard(e) {
    e.preventDefault();
    if (this.hasWalletSectionTarget) this.walletSectionTarget.classList.add("hidden");
    this.restoreDisclosure();
    await this.revealCard();
  }

  disclosureCopyElement() {
    return this.hasDisclosureTarget ? this.disclosureTarget.querySelector("[data-tip-disclosure-copy]") : null;
  }

  disclosureCopyText() {
    const el = this.disclosureCopyElement();
    return el ? el.textContent.trim() : "";
  }

  showWalletDisclosure() {
    const el = this.disclosureCopyElement();
    if (el) el.textContent = `You'll confirm this tip with ${this.walletLabel || "your wallet"}.`;
  }

  restoreDisclosure() {
    const el = this.disclosureCopyElement();
    if (el && this.defaultDisclosureText != null) el.textContent = this.defaultDisclosureText;
  }

  // --- helpers --------------------------------------------------------------

  chatPanelController() {
    const el = this.element.closest('[data-controller~="chat-panel"]');
    if (!el) return null;
    return this.application.getControllerForElementAndIdentifier(el, "chat-panel");
  }

  // True when the failure is the waitForStripe timeout (Stripe.js blocked / failed to load).
  stripeUnavailable(error) {
    return !!error && error.message === "stripe_unavailable";
  }

  // Freeze the amount and keep the current client_tip_id so a same-amount retry reuses the
  // idempotency key (dedupe against the original charge); a changed amount is blocked. Cleared by
  // onTipBroadcast when the pin arrives, or onSucceeded on retry.
  lockAmount(message) {
    this.amountLocked = true;
    this.setAmountEditingDisabled(true);
    this.showError(message);
    this.setInFlight(false);
  }

  unlockAmount() {
    this.amountLocked = false;
    this.setAmountEditingDisabled(false);
  }

  setAmountEditingDisabled(disabled) {
    this.presetTargets.forEach((chip) => { chip.disabled = disabled; });
    if (this.hasCustomTarget) this.customTarget.disabled = disabled;
  }

  ensureClientTipId() {
    if (!this.clientTipId) this.clientTipId = this.newUuid();
    return this.clientTipId;
  }

  rotateClientTipId() {
    this.clientTipId = null;
    this.chargedCents = null;
    this.chargedPaymentMethodId = null;
  }

  newUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return window.crypto.randomUUID();
    return `t-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  setInFlight(value) {
    this.inFlight = value;
    if (this.hasSendButtonTarget) this.sendButtonTarget.disabled = value;
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
    this.setPanelOpen(true); // make sure the panel (and its error) is visible
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }
}
